import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IContractor,
  IContractorDuplicatePair,
  IContractorMergeRequest,
  ICreateContractorPayload,
  IUpdateContractorPayload,
  IContractorDuplicateCheckResult,
  IMergeHistoryEntry,
} from "dto/Contractors/IContractor";

// ── Funkcje API ──────────────────────────────────────────────────────────────

const listContractorsApi = async (params?: {
  q?: string;
  includeDuplicates?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<IContractor[]> =>
  (await axiosInstance.get<IContractor[]>("/contractors", { params })).data;

const getContractorApi = async (id: string): Promise<IContractor> =>
  (await axiosInstance.get<IContractor>(`/contractors/${id}`)).data;

const findContractorApi = async (params: {
  nip?: string;
  pesel?: string;
  regon?: string;
  iban?: string;
}): Promise<IContractor | null> => {
  try {
    return (await axiosInstance.get<IContractor>("/contractors/find", { params })).data;
  } catch {
    return null;
  }
};

const createContractorApi = async (
  payload: ICreateContractorPayload,
): Promise<IContractor & { DuplicateCheck?: IContractorDuplicateCheckResult }> =>
  (await axiosInstance.post(`/contractors`, payload)).data;

const updateContractorApi = async (
  id: string,
  payload: IUpdateContractorPayload,
): Promise<IContractor> =>
  (await axiosInstance.put<IContractor>(`/contractors/${id}`, payload)).data;

const deleteContractorApi = async (id: string): Promise<void> =>
  axiosInstance.delete(`/contractors/${id}`);

const getDuplicatesApi = async (): Promise<IContractorDuplicatePair[]> =>
  (await axiosInstance.get<IContractorDuplicatePair[]>("/contractors/duplicates")).data;

const mergeContractorsApi = async (req: IContractorMergeRequest): Promise<{ MergeLogId: string }> =>
  (await axiosInstance.post<{ MergeLogId: string }>("/contractors/merge", req)).data;

const undoMergeApi = async (mergeLogId: string): Promise<void> =>
  axiosInstance.post("/contractors/merge/undo", { MergeLogId: mergeLogId });

const getMergeHistoryApi = async (contractorId: string): Promise<IMergeHistoryEntry[]> =>
  (await axiosInstance.get<IMergeHistoryEntry[]>(`/contractors/${contractorId}/merge-history`)).data;

const reconcileInvoicesApi = async (): Promise<{ Linked: number; Created: number }> =>
  (await axiosInstance.post("/contractors/reconcile/invoices")).data;

const reconcileBankApi = async (): Promise<{ Linked: number; Created: number }> =>
  (await axiosInstance.post("/contractors/reconcile/bank")).data;

const reconcileContractsApi = async (): Promise<{ Linked: number; Created: number }> =>
  (await axiosInstance.post("/contractors/reconcile/contracts")).data;

export interface IContractorImportItem {
  Name: string;
  FullName?: string;
  NIP?: string;
  PESEL?: string;
  REGON?: string;
  Address?: string;
  PrimaryIban?: string;
  AccountingCode?: string;
}

export interface IContractorImportResult {
  Created: number;
  Updated: number;
  Skipped: number;
}

const importContractorsApi = async (
  items: IContractorImportItem[],
): Promise<IContractorImportResult> =>
  (await axiosInstance.post("/contractors/import", { Items: items })).data;

export interface IContractorDedupeResult {
  Groups: number;
  Merged: number;
}

const dedupeContractorsApi = async (): Promise<IContractorDedupeResult> =>
  (await axiosInstance.post("/contractors/dedupe")).data;

export interface IContractorResetResult {
  Removed: number;
}

const resetContractorsApi = async (): Promise<IContractorResetResult> =>
  (await axiosInstance.post("/contractors/reset")).data;

export interface IContractorFillAccountingCodesResult {
  Filled: number;
}

const fillAccountingCodesApi = async (): Promise<IContractorFillAccountingCodesResult> =>
  (await axiosInstance.post("/contractors/accounting-codes/fill")).data;

// ── React Query hooks ────────────────────────────────────────────────────────

const Q_CONTRACTORS = "contractors";
const Q_CONTRACTOR  = "contractor";
const Q_DUPLICATES  = "contractors-duplicates";

const contractorsService = {
  useList: (params?: { q?: string; includeDuplicates?: boolean }) =>
    useCustomQuery<IContractor[]>(
      [Q_CONTRACTORS, params],
      () => listContractorsApi(params),
    ),

  useGet: (id: string, enabled = true) =>
    useCustomQuery<IContractor>(
      [Q_CONTRACTOR, id],
      () => getContractorApi(id),
      { enabled: enabled && !!id },
    ),

  /** Wyszukuje kontrahenta po jednym z identyfikatorów. Używane przy wpisywaniu NIP/PESEL w formularzu faktury. */
  useFindByIdentifier: (params: { nip?: string; pesel?: string; regon?: string; iban?: string }, enabled = true) =>
    useCustomQuery<IContractor | null>(
      [Q_CONTRACTORS, "find", params],
      () => findContractorApi(params),
      {
        enabled: enabled && !!(params.nip || params.pesel || params.regon || params.iban),
        staleTime: 60_000,
      },
    ),

  useCreate: () => {
    const qc = useQueryClient();
    return useCustomMutation(
      (payload: ICreateContractorPayload) => createContractorApi(payload),
      { onSuccess: () => qc.invalidateQueries(Q_CONTRACTORS) },
    );
  },

  useUpdate: () => {
    const qc = useQueryClient();
    return useCustomMutation(
      ({ id, payload }: { id: string; payload: IUpdateContractorPayload }) =>
        updateContractorApi(id, payload),
      {
        onSuccess: () => {
          qc.invalidateQueries(Q_CONTRACTORS);
          qc.invalidateQueries(Q_CONTRACTOR);
        },
      },
    );
  },

  useDelete: () => {
    const qc = useQueryClient();
    return useCustomMutation(
      (id: string) => deleteContractorApi(id),
      { onSuccess: () => qc.invalidateQueries(Q_CONTRACTORS) },
    );
  },

  useDuplicates: () =>
    useCustomQuery<IContractorDuplicatePair[]>(
      [Q_DUPLICATES],
      getDuplicatesApi,
      { staleTime: 30_000 },
    ),

  useMerge: () => {
    const qc = useQueryClient();
    return useCustomMutation<{ MergeLogId: string }, IContractorMergeRequest>(
      (req) => mergeContractorsApi(req),
      {
        onSuccess: () => {
          qc.invalidateQueries(Q_CONTRACTORS);
          qc.invalidateQueries(Q_DUPLICATES);
        },
      },
    );
  },

  useMergeHistory: (contractorId: string, enabled = true) =>
    useCustomQuery<IMergeHistoryEntry[]>(
      [Q_CONTRACTORS, "merge-history", contractorId],
      () => getMergeHistoryApi(contractorId),
      { enabled: enabled && !!contractorId, staleTime: 30_000 },
    ),

  useUndoMerge: () => {
    const qc = useQueryClient();
    return useCustomMutation<void, string>(
      (mergeLogId) => undoMergeApi(mergeLogId),
      {
        onSuccess: () => {
          qc.invalidateQueries(Q_CONTRACTORS);
          qc.invalidateQueries(Q_DUPLICATES);
        },
      },
    );
  },

  /** Import zbiorczy kartoteki z pliku migracji (upsert po NIP/PESEL/REGON/IBAN). */
  useImport: () => {
    const qc = useQueryClient();
    return useCustomMutation<IContractorImportResult, IContractorImportItem[]>(
      (items) => importContractorsApi(items),
      { onSuccess: () => qc.invalidateQueries(Q_CONTRACTORS) },
    );
  },

  /** Odgruzowanie kartoteki — scala wszystkie duble po NIP/IBAN. */
  useDedupe: () => {
    const qc = useQueryClient();
    return useCustomMutation<IContractorDedupeResult, void>(
      () => dedupeContractorsApi(),
      {
        onSuccess: () => {
          qc.invalidateQueries(Q_CONTRACTORS);
          qc.invalidateQueries(Q_DUPLICATES);
        },
      },
    );
  },

  useReconcileInvoices: () =>
    useCustomMutation<{ Linked: number; Created: number }, void>(() => reconcileInvoicesApi()),

  useReconcileBank: () =>
    useCustomMutation<{ Linked: number; Created: number }, void>(() => reconcileBankApi()),

  useReconcileContracts: () =>
    useCustomMutation<{ Linked: number; Created: number }, void>(() => reconcileContractsApi()),

  /** Uzupełnia kody kont analitycznych (201/211) dla kontrahentów bez kodu — idempotentne. */
  useFillAccountingCodes: () => {
    const qc = useQueryClient();
    return useCustomMutation<IContractorFillAccountingCodesResult, void>(
      () => fillAccountingCodesApi(),
      { onSuccess: () => qc.invalidateQueries(Q_CONTRACTORS) },
    );
  },

  /** Czyści całą kartotekę kontrahentów (soft-delete) i zeruje FK — umożliwia przebudowę przez reconcile. */
  useReset: () => {
    const qc = useQueryClient();
    return useCustomMutation<IContractorResetResult, void>(
      () => resetContractorsApi(),
      {
        onSuccess: () => {
          qc.invalidateQueries(Q_CONTRACTORS);
          qc.invalidateQueries(Q_DUPLICATES);
        },
      },
    );
  },
};

export default contractorsService;
